import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

export default function SdkView() {
  const { proj, activeId, apiFetch, dark, t, card, inp, btnP, btnG, flash } = useApp();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maxLinks, setMaxLinks] = useState(15);
  const [replaceH1, setReplaceH1] = useState(true);
  const [config, setConfig] = useState({ meta: false, h1: false, cocoon: false, schema: false, dateModified: false, contentCategory: false, contentProduct: false, contentBlog: false, cocoonExcludePaths: [], cocoonOnlyPaths: [] });
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [autoMeta, setAutoMeta] = useState(null);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [specialist, setSpecialist] = useState(null);
  const [specBusy, setSpecBusy] = useState(false);
  const [showDossier, setShowDossier] = useState(false);

  // Load or create token
  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    apiFetch(`/api/projects/${activeId}/sdk/token`)
      .then(r => r.json())
      .then(d => { if (d.token) setToken(d.token); if (d.config) setConfig(prev => ({ ...prev, ...d.config })); })
      .catch(() => {})
      .finally(() => setLoading(false));
    apiFetch(`/api/projects/${activeId}/auto-meta`).then(r => r.json()).then(d => { if (d) setAutoMeta(d); }).catch(() => {});
    apiFetch(`/api/projects/${activeId}/specialist/status`).then(r => r.json()).then(d => { if (d) setSpecialist(d); }).catch(() => {});
  }, [activeId, apiFetch]);

  // Створити/перебудувати бота-спеціаліста (async + поллінг статусу)
  const buildSpecialist = async () => {
    setSpecBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/specialist/build`, { method: "POST" });
      const d = await r.json();
      if (d.error) { flash("Помилка: " + d.error); setSpecBusy(false); return; }
      flash("Бот-спеціаліст створюється — вивчає сторінки й нішу...");
      const poll = setInterval(async () => {
        try {
          const s = await (await apiFetch(`/api/projects/${activeId}/specialist/status`)).json();
          setSpecialist(s);
          if (s.status === "ready" || s.status === "failed") {
            clearInterval(poll); setSpecBusy(false);
            flash(s.status === "ready" ? "Бот-спеціаліст готовий ✓" : "Помилка бота: " + (s.error || ""));
          }
        } catch {}
      }, 4000);
    } catch (e) { flash("Помилка: " + e.message); setSpecBusy(false); }
  };
  const specStageLabel = { queued: "у черзі", studying_pages: "вивчає сторінки", researching_niche: "досліджує нішу й конкурентів", building_dossier: "складає досьє", ready: "готовий", error: "помилка" };

  const generateToken = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/sdk/token`, { method: "POST" });
      const d = await r.json();
      if (d.token) { setToken(d.token); if (d.config) setConfig(prev => ({ ...prev, ...d.config })); flash("Токен створено"); }
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const toggleConfig = async (key) => {
    const next = { ...config, [key]: !config[key] };
    setConfig(next);
    try {
      await apiFetch(`/api/projects/${activeId}/sdk/config`, { method: "PUT", body: JSON.stringify(next) });
    } catch {}
  };

  const saveConfig = async (next) => {
    setConfig(next);
    try {
      await apiFetch(`/api/projects/${activeId}/sdk/config`, { method: "PUT", body: JSON.stringify(next) });
    } catch {}
  };

  const checkSync = async () => {
    if (!token || !proj.domain) return;
    setChecking(true);
    setCheckResult(null);
    const results = { sdk: false, api: false, installed: false, meta: 0, cocoon: 0, content: 0 };

    // 1. Check SDK file loads
    try {
      const r = await fetch(`https://spalah.app/sdk.js?t=${token}`, { signal: AbortSignal.timeout(10000) });
      results.sdk = r.ok && r.headers.get("content-type")?.includes("javascript");
    } catch {}

    // 2. Check API returns data
    try {
      const r = await fetch(`https://spalah.app/api/public/seo/${token}`, { signal: AbortSignal.timeout(10000) });
      if (r.ok) {
        const d = await r.json();
        results.api = true;
        results.meta = Object.keys(d.meta || {}).length;
        results.cocoon = (d.pages || []).length;
        results.content = Object.keys(d.content || {}).length;
      }
    } catch {}

    // 3. Verify the LIVE site actually has the sdk.js script with THIS project's token
    //    (server-side fetch of the client page — не просто «є якийсь sdk.js», а саме наш токен)
    try {
      const r = await apiFetch(`/api/projects/${activeId}/sdk/verify-install`);
      const d = await r.json();
      results.siteReachable = d.siteReachable !== false;
      results.installed = !!d.installed;         // на сайті є якийсь Spalah SDK
      results.tokenMatches = !!d.tokenMatches;   // саме токен ЦЬОГО проєкту
      results.otherTokens = d.otherTokens || [];
      results.verifyErr = d.error || null;
    } catch { results.siteReachable = false; results.verifyErr = "check_failed"; }

    setCheckResult(results);
    setChecking(false);
  };

  if (!proj) return null;

  const publicHost = "spalah.app";
  const sdkUrl = token ? `https://${publicHost}/sdk.js?t=${token}` : null;
  const sdkLine = sdkUrl ? `<script src="${sdkUrl}"${!replaceH1 ? ' data-h1="false"' : ''}${maxLinks !== 15 ? ` data-links="${maxLinks}"` : ''} async defer></script>` : "";
  const fullSnippet = sdkUrl ? `<!-- Spalah SEO SDK -->\n\n<!-- Блок кокону — вставити де потрібен блок посилань (перед footer) -->\n<div id="spalah-seo"></div>\n\n<!-- SDK скрипт — вставити перед </body> -->\n${sdkLine}` : "";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Spalah SDK</h2>
        <span style={{ fontSize: 12, color: t.textMuted }}>{proj.domain}</span>
      </div>

      {loading ? (
        <div style={{ ...card, textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження...</div>
      ) : !token ? (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔌</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Підключіть сайт до Spalah</p>
          <p style={{ fontSize: 13, color: t.textMuted, marginBottom: 16, maxWidth: 460, margin: "0 auto 16px" }}>
            Один скрипт для сайту: заміна мета-тегів + SEO кокон. Працює з Weblium, WordPress, Webflow, Shopify, будь-якою CMS.
          </p>
          <button onClick={generateToken} style={btnP}>Створити токен підключення</button>
        </div>
      ) : (
        <>
          {/* Code to copy */}
          <div style={{
            ...card, padding: "24px 28px", textAlign: "center", borderRadius: 16,
            background: dark ? "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.04))" : "linear-gradient(135deg, rgba(99,102,241,0.04), rgba(139,92,246,0.02))",
            border: `1px solid ${dark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)"}`,
          }}>
            <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>Інструкція встановлення</div>
            <div style={{ background: dark ? "#0a0a10" : "#f8f9fb", borderRadius: 10, padding: "16px 18px", textAlign: "left", margin: "0 0 14px" }}>
              <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 12, lineHeight: 1.5 }}>Для розробника сайту. Майже все — це «скопіювати готові рядки і вставити». Своє підставляєте лише в КРОК 4 (CSS-селектори — пояснення нижче).</div>

              <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, marginBottom: 6 }}>КРОК 1 — місце для блоку перелінковки. Вставте цей рядок у тілі сторінки там, де хочете бачити блок посилань (зазвичай прямо над футером):</div>
              <pre style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-all", color: t.text, margin: "0 0 14px", padding: 0, background: "transparent" }}>{`<div id="spalah-seo"></div>`}</pre>

              <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, marginBottom: 6 }}>КРОК 2 — головний скрипт Spalah. Вставте в самому низу сторінки, прямо перед закриваючим тегом &lt;/body&gt;:</div>
              <pre style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#6366f1", fontWeight: 600, margin: 0, padding: 0, background: "transparent" }}>{sdkLine}</pre>

              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${dark ? "#1e1e2c" : "#e5e7eb"}` }}>
                <div style={{ fontSize: 11, color: "#10b981", fontWeight: 700, marginBottom: 6 }}>КРОК 3 (опційно) — картки і фільтри для футера. Вставте ці рядки В ТОМУ САМОМУ місці, що й головний скрипт (унизу, перед &lt;/body&gt;), бажано ТРОХИ ВИЩЕ за рядок sdk.js. Це два окремі рядки — додайте обидва або лише потрібний. У лапках замість прикладу впишіть свій селектор (див. нижче):</div>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, margin: "2px 0 4px" }}>Картки товарів/категорій:</div>
                <pre style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#10b981", fontWeight: 600, margin: "0 0 10px", padding: 0, background: "transparent" }}>{`<script>window.SpalahCards = ".product-card a";</script>`}</pre>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, margin: "2px 0 4px" }}>Фільтри:</div>
                <pre style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#10b981", fontWeight: 600, margin: 0, padding: 0, background: "transparent" }}>{`<script>window.SpalahFilters = ".filter a";</script>`}</pre>

                <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: dark ? "rgba(16,185,129,0.06)" : "rgba(16,185,129,0.05)", border: `1px solid ${dark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.12)"}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.text, marginBottom: 6 }}>Що таке «селектор» і де його взяти (просто):</div>
                  <div style={{ fontSize: 11.5, color: t.text, lineHeight: 1.7 }}>
                    Селектор — це адреса елемента за його класом. Картка товару на сайті — це посилання типу <code style={{ background: dark ? "#252535" : "#eef", padding: "1px 4px", borderRadius: 3 }}>&lt;a class="product-card" href="..."&gt;Назва&lt;/a&gt;</code>. Тоді селектор = <code style={{ background: dark ? "#252535" : "#eef", padding: "1px 4px", borderRadius: 3 }}>.product-card</code> (клас із крапкою попереду).
                    <div style={{ marginTop: 6 }}><strong>Як знайти за 10 секунд:</strong> відкрийте сторінку категорії → правий клік по назві товару → «Перевірити код» / Inspect → подивіться клас тега <code style={{ background: dark ? "#252535" : "#eef", padding: "1px 4px", borderRadius: 3 }}>&lt;a&gt;</code> → впишіть його з крапкою. Так само для фільтра.</div>
                    <div style={{ marginTop: 6 }}>Приклади в рядках вище (<code>.product-card</code>, <code>.filter</code>) — це ЗРАЗОК, замініть на свої класи. Можна кілька через кому: <code>.product-card a, .card-title a</code>.</div>
                    <div style={{ marginTop: 6 }}><strong>Важливо:</strong> селектор вішається на ПОСИЛАННЯ в каталозі/категоріях (анкор, що веде на товар або застосовує фільтр) — НЕ на сторінці самого товару. У футері показується текст цього анкора.</div>
                    <div style={{ marginTop: 6, color: t.textMuted }}>Більше нічого робити не треба: Spalah сам знайде всі картки/фільтри за селектором і збере їх для блоків «Топ-картки» / «Топ-фільтри». Жодних правок на кожну картку.</div>
                  </div>
                </div>

                <div style={{ fontSize: 11, color: "#0ea5e9", fontWeight: 700, margin: "16px 0 6px" }}>КРОК 4 (опційно) — редагування текстів «на місці». Обгорніть наявний текст на сторінці унікальним незмінним slug — Spalah підмінить ЛИШЕ текст, не чіпаючи верстку та класи (порожній блок — впише новий текст):</div>
                <pre style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-all", color: t.text, margin: 0, padding: 0, background: "transparent" }}>{`<div data-spalah="services-intro">\n  <h2>Наші послуги</h2>\n  <p>Існуючий текст сторінки…</p>\n  <ul><li>Пункт 1</li><li>Пункт 2</li></ul>\n</div>`}</pre>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button onClick={() => { navigator.clipboard.writeText(fullSnippet); flash("Скопійовано!"); }} style={{ ...btnP, fontSize: 13 }}>Копіювати весь код</button>
              <button onClick={checkSync} disabled={checking} style={{ ...btnG, fontSize: 13, opacity: checking ? 0.6 : 1 }}>
                {checking ? "Перевіряю..." : "Перевірити підключення"}
              </button>
            </div>
            <div style={{ textAlign: "left", marginTop: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.text, marginBottom: 8 }}>Як редагувати наявний текст сайту (КРОК 4):</div>
              {[
                { n: "1", txt: "Додайте розмітку — один <div data-spalah=\"slug\"> навколо наявного тексту (щоб редагувати його) АБО порожній (щоб Spalah вписав новий). Кілька блоків = кілька div з різними slug. Працює на будь-якому движку (Elementor, Tilda, semantic-теми)." },
                { n: "2", txt: "У SEO Редакторі виділіть сторінки галочками → кнопка «⬇ Підтягнути з сайту» → текст підтягнеться в редактор." },
                { n: "3", txt: "Редагуйте / перепишіть і натисніть «Опублікувати» — Spalah підмінить ЛИШЕ текст, верстка та класи теми/конструктора лишаться недоторканими." },
              ].map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 8, padding: "4px 0", alignItems: "baseline" }}>
                  <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, width: 18, height: 18, lineHeight: "18px", textAlign: "center", borderRadius: "50%", background: "rgba(14,165,233,0.12)", color: "#0ea5e9" }}>{s.n}</span>
                  <span style={{ fontSize: 12, color: t.text, lineHeight: 1.6 }}>{s.txt}</span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: t.textMuted, lineHeight: 1.6, marginTop: 8 }}>
                Для нового тексту лишіть порожній <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>&lt;div data-spalah="slug"&gt;&lt;/div&gt;</code> — Spalah впише туди. Додавання абзацу того ж типу — безпечне (клонується сусідній елемент зі стилями).<br />
                <span style={{ opacity: 0.85 }}>Зворотна сумісність: старі сайти з блоком <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>&lt;div id="spalah-content"&gt;</code> продовжують працювати — нічого не ламається. Нові правила — для нових підключень.</span>
              </div>
            </div>
          </div>

          {/* Check result */}
          {checkResult && (
            <div style={{ ...card, padding: 0, overflow: "hidden", marginTop: 4 }}>
              <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Статус підключення</span>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 8, marginLeft: "auto",
                  background: checkResult.tokenMatches && checkResult.api ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                  color: checkResult.tokenMatches && checkResult.api ? "#10b981" : "#ef4444",
                }}>
                  {checkResult.tokenMatches && checkResult.api ? "Підключено · токен збігається" : "Є проблеми"}
                </span>
              </div>
              {[
                { label: "Скрипт на сайті (токен)", ok: checkResult.tokenMatches, desc:
                    checkResult.tokenMatches ? "На сайті стоїть скрипт із токеном ЦЬОГО проєкту ✓"
                    : checkResult.siteReachable === false ? `Сайт недоступний для перевірки${checkResult.verifyErr ? ` (${checkResult.verifyErr})` : ""}`
                    : (checkResult.otherTokens && checkResult.otherTokens.length) ? `На сайті ІНШИЙ токен: ${checkResult.otherTokens[0].slice(0, 8)}… — цей проєкт НЕ підключений`
                    : checkResult.installed ? "Spalah SDK є, але без токена — перевірте рядок скрипта"
                    : "Скрипт Spalah на сайті не знайдено" },
                { label: "SDK файл (sdk.js)", ok: checkResult.sdk, desc: checkResult.sdk ? "Завантажується через CloudFlare CDN" : "Не вдалось завантажити — перевірте тунель" },
                { label: "API даних", ok: checkResult.api, desc: checkResult.api ? "Відповідає коректно" : "Не відповідає — перевірте чи сервер працює" },
                { label: "Мета-теги", ok: checkResult.meta > 0, desc: checkResult.meta > 0 ? `${checkResult.meta} URL з мета-даними` : "Немає мета — згенеруйте в модулі Meta-теги", warn: !config.meta },
                { label: "SEO Кокон", ok: checkResult.cocoon > 0, desc: checkResult.cocoon > 0 ? `${checkResult.cocoon} сторінок в коконі` : "Немає — згенеруйте в модулі SEO кокон реалізація", warn: !config.cocoon },
                { label: "Тексти контенту", ok: checkResult.content > 0, desc: checkResult.content > 0 ? `${checkResult.content} сторінок з текстами` : "Немає — згенеруйте в модулі Контент", warn: !(config.contentCategory || config.contentProduct || config.contentBlog) },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 6, flexShrink: 0,
                    background: item.ok ? "rgba(16,185,129,0.1)" : item.warn ? "rgba(100,116,139,0.1)" : "rgba(239,68,68,0.1)",
                    color: item.ok ? "#10b981" : item.warn ? "#64748b" : "#ef4444",
                  }}>
                    {item.ok ? "OK" : item.warn ? "ВИКЛ" : "!"}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{item.label}</div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>{item.desc}</div>
                  </div>
                  {item.warn && <span style={{ fontSize: 9, color: t.textMuted }}>Вимкнено в налаштуваннях</span>}
                </div>
              ))}
            </div>
          )}

          {/* Module toggles */}
          <div style={{ ...card, padding: 0, overflow: "hidden", marginTop: 4 }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border}`, fontSize: 14, fontWeight: 700 }}>Що розмістити на сайті</div>
            {[
              { key: "meta", icon: "🏷", title: "Title + Description", desc: "Замінює Title та Description на кожній сторінці з семантики Spalah" },
              { key: "h1", icon: "🔤", title: "H1 заголовок", desc: "Замінює H1 на сторінці на згенерований в Spalah" },
              { key: "cocoon", icon: "🕸", title: "SEO Кокон", desc: `Блок з ${maxLinks} релевантних посилань — підібраних по семантичній схожості` },
              { key: "schema", icon: "📋", title: "BreadcrumbList Schema", desc: "Автоматичні хлібні крихти в розмітці — Google показує в видачі" },
              { key: "dateModified", icon: "📅", title: "Дата оновлення", desc: "Badge 'Оновлено: дата' біля H1 + schema dateModified — буст свіжості контенту" },
              { key: "contentCategory", icon: "📂", title: "Тексти — Категорії", desc: "Вставляє згенеровані тексти для сторінок категорій" },
              { key: "contentProduct", icon: "🛍", title: "Тексти — Товари", desc: "Вставляє згенеровані тексти для карток товарів" },
              { key: "contentBlog", icon: "📝", title: "Тексти — Статті", desc: "Вставляє згенеровані тексти для блогу/статей" },
              { key: "cocoonAttr", icon: "©️", title: "Підпис «powered by Spalah»", desc: "Показувати підпис «powered by Spalah · Svitlo agency product» під блоком SEO-кокона" },
            ].map(item => (
              <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{item.title}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 1 }}>{item.desc}</div>
                </div>
                <button onClick={() => toggleConfig(item.key)} style={{
                  padding: "6px 16px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                  border: "none", transition: "all .2s",
                  background: config[item.key] ? "linear-gradient(135deg, #10b981, #059669)" : (dark ? "#252535" : "#e5e7eb"),
                  color: config[item.key] ? "#fff" : t.textMuted,
                  boxShadow: config[item.key] ? "0 2px 8px rgba(16,185,129,0.3)" : "none",
                }}>
                  {config[item.key] ? "Увімкнено" : "Вимкнено"}
                </button>
              </div>
            ))}
            <div style={{ padding: "10px 18px", display: "flex", gap: 12, alignItems: "center" }}>
              <div>
                <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4 }}>Посилань в коконі</label>
                <input type="number" min={5} max={30} value={maxLinks} onChange={e => setMaxLinks(parseInt(e.target.value, 10) || 15)}
                  style={{ ...inp, width: 60, fontSize: 12, padding: "5px 8px", textAlign: "center" }} />
              </div>
              <div style={{ fontSize: 10, color: t.textMuted, marginLeft: "auto" }}>
                Токен: <code style={{ color: "#6366f1" }}>{token.slice(0, 8)}...</code>
              </div>
            </div>
            <div style={{ padding: "10px 18px 14px", borderTop: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
              <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", marginBottom: 4 }}>Не показувати кокон на шляхах</label>
              <input type="text"
                value={(config.cocoonExcludePaths || []).join(", ")}
                onChange={e => setConfig(prev => ({ ...prev, cocoonExcludePaths: e.target.value.split(/[,\n]/).map(s => s.trim()).filter(Boolean) }))}
                onBlur={() => saveConfig(config)}
                placeholder="/ru/, /en/, /cart"
                style={{ ...inp, width: "100%", fontSize: 12, padding: "6px 10px" }} />
              <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 5 }}>
                Через кому. Якщо URL сторінки містить будь-який із цих підрядків — кокон не рендериться (напр. <code>/ru/</code> вимкне кокон на всій ру-версії). Порожньо → показувати скрізь.
              </div>
              <label style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, display: "block", margin: "12px 0 4px" }}>Показувати кокон ТІЛЬКИ на шляхах</label>
              <input type="text"
                value={(config.cocoonOnlyPaths || []).join(", ")}
                onChange={e => setConfig(prev => ({ ...prev, cocoonOnlyPaths: e.target.value.split(/[,\n]/).map(s => s.trim()).filter(Boolean) }))}
                onBlur={() => saveConfig(config)}
                placeholder="/ru/"
                style={{ ...inp, width: "100%", fontSize: 12, padding: "6px 10px" }} />
              <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 5 }}>
                Білий список. Якщо заповнено — кокон показується <b>ЛИШЕ</b> на сторінках, чий шлях містить один із підрядків (напр. <code>/ru/</code> → кокон тільки на ру-версії, на укр-версії ховається). Порожньо → без обмежень.
              </div>
            </div>
          </div>

          {/* Auto-meta for remaining pages */}
          <div style={{ ...card, padding: 0, overflow: "hidden", marginTop: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: `1px solid ${t.border}` }}>
              <span style={{ fontSize: 20 }}>⚡</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Авто-мета для решти сторінок</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>
                  Генерує Title + Description для сторінок які НЕ в семантиці (по формулі з H1).
                  {autoMeta && <span> Згенеровано: <strong>{autoMeta.pages?.length || 0}</strong> сторінок ({autoMeta.generatedAt?.slice(0, 10)})</span>}
                </div>
              </div>
              <button onClick={async () => {
                setAutoGenerating(true);
                try {
                  const token = localStorage.getItem("metago_token");
                  const resp = await fetch(`/api/projects/${activeId}/auto-meta/generate`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ brand: proj.name, city: "Київ" }),
                  });
                  const text = await resp.text();
                  const lines = text.split("\n");
                  for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    try {
                      const msg = JSON.parse(line.slice(6));
                      if (msg.type === "progress") setAutoMeta(prev => ({ ...(prev || {}), progress: msg.step }));
                      else if (msg.type === "done") {
                        flash(`Згенеровано мета для ${msg.count} сторінок (sitemap: ${msg.sitemapUrls || 0}, ручна мета: ${msg.withManualMeta || 0})`);
                        setAutoMeta({ count: msg.count, generatedAt: new Date().toISOString() });
                      } else if (msg.type === "error") flash("Помилка: " + msg.error);
                    } catch {}
                  }
                } catch (e) { flash("Помилка: " + e.message); }
                setAutoGenerating(false);
              }} disabled={autoGenerating} style={{ ...btnP, fontSize: 12, opacity: autoGenerating ? 0.6 : 1, flexShrink: 0 }}>
                {autoGenerating ? (autoMeta?.progress || "Генерую...") : "Згенерувати"}
              </button>
            </div>
            <div style={{ padding: "10px 18px", fontSize: 11, color: t.textMuted, lineHeight: 1.6 }}>
              <strong>Як працює:</strong> Бере H1 кожної сторінки з Сканера сайту → формує Title: "<em>H1 — Назва бренду</em>" і Description з CTA.
              Якщо потім додаєте сторінку в семантику і генеруєте мета вручну — ручна мета замінить автоматичну.
            </div>
          </div>

          {/* Specialist bot */}
          <div style={{ ...card, padding: 0, overflow: "hidden", marginTop: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: `1px solid ${t.border}` }}>
              <span style={{ fontSize: 20 }}>🧠</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Бот-спеціаліст по проєкту</div>
                <div style={{ fontSize: 11, color: t.textMuted }}>
                  Вивчає кожну сторінку структури, розуміє бренд, досліджує нішу й конкурентів і складає досьє.
                  Потім фінально перевіряє тексти Контент-заводу на відповідність фактам.
                  {specialist?.status === "ready" && <span> Готовий · вивчено <strong>{specialist.pages_studied || 0}/{specialist.pages_total || 0}</strong> сторінок.</span>}
                  {specialist && specialist.status !== "ready" && specialist.status !== "idle" && <span> Статус: <strong>{specStageLabel[specialist.stage] || specialist.status}</strong> ({specialist.pages_studied || 0}/{specialist.pages_total || 0}).</span>}
                </div>
              </div>
              <button onClick={buildSpecialist} disabled={specBusy || specialist?.status === "running" || specialist?.status === "queued"}
                style={{ ...btnP, fontSize: 12, opacity: (specBusy || specialist?.status === "running" || specialist?.status === "queued") ? 0.6 : 1, flexShrink: 0 }}>
                {(specBusy || specialist?.status === "running" || specialist?.status === "queued")
                  ? (specStageLabel[specialist?.stage] || "Створюю...")
                  : (specialist?.status === "ready" ? "Перебудувати" : "Створити бота-спеціаліста")}
              </button>
            </div>
            {specialist?.status === "ready" && specialist.dossier ? (
              <div style={{ padding: "10px 18px" }}>
                <button onClick={() => setShowDossier(v => !v)} style={{ ...btnG, fontSize: 11, padding: "5px 10px" }}>
                  {showDossier ? "Сховати досьє" : "Переглянути досьє"}
                </button>
                {showDossier && (
                  <pre style={{ marginTop: 10, maxHeight: 360, overflow: "auto", whiteSpace: "pre-wrap", fontSize: 11.5, lineHeight: 1.55, color: t.text, background: dark ? "#14141f" : "#f7f7f9", padding: 12, borderRadius: 8, border: `1px solid ${t.border}` }}>
                    {specialist.dossier}
                  </pre>
                )}
              </div>
            ) : (
              <div style={{ padding: "10px 18px", fontSize: 11, color: t.textMuted, lineHeight: 1.6 }}>
                <strong>Як працює:</strong> бере URL+ключі всіх сторінок структури, тягне головну й ключові сторінки, серчить нішу через Tavily → формує досьє бренду. У Контент-заводі стає останнім гейтом: звіряє написане з фактами бренду й ніші.
              </div>
            )}
          </div>

          {/* What it does */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border}`, fontSize: 14, fontWeight: 700 }}>Що робить SDK</div>
            {[
              { icon: "🏷", title: "Замінює Title та Description", desc: "Підставляє мета-теги згенеровані в Spalah для кожної сторінки сайту" },
              { icon: "📝", title: "Замінює H1", desc: "Підставляє H1 зі Spalah (можна вимкнути в налаштуваннях)" },
              { icon: "🕸", title: "SEO кокон", desc: `Блок з ${maxLinks} релевантних посилань, підібраних по семантичній схожості` },
              { icon: "💾", title: "Офлайн кеш", desc: "Все кешується в localStorage. Якщо Spalah недоступний — сайт працює з кешу" },
              { icon: "⚡", title: "Не гальмує сайт", desc: "async + defer — завантажується після основного контенту, не блокує рендеринг" },
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                <span style={{ fontSize: 20 }}>{f.icon}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{f.title}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CMS instructions */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border}`, fontSize: 14, fontWeight: 700 }}>Встановлення по CMS</div>
            {[
              { cms: "Weblium", icon: "🟡", steps: "Налаштування сайту → Код на всіх сторінках → Before </body> → вставити КРОК 2. Для кокону: додати блок 'HTML код' на сторінку → вставити КРОК 1." },
              { cms: "WordPress", icon: "🔵", steps: "Вигляд → Редактор тем → footer.php → вставити перед </body>. Або плагін 'Insert Headers and Footers' → Footer Scripts." },
              { cms: "OpenCart", icon: "🟢", steps: "Catalog → Modify → common/footer.twig → вставити перед </body>. Або Extensions → Custom Code." },
              { cms: "Shopify", icon: "🟣", steps: "Online Store → Themes → Edit code → theme.liquid → вставити перед </body>." },
              { cms: "Webflow", icon: "🟠", steps: "Project Settings → Custom Code → Before </body> tag → вставити КРОК 2. Для кокону: Embed element → КРОК 1." },
              { cms: "HTML / Інша CMS", icon: "⚪", steps: "Знайти загальний шаблон (layout/base), вставити код перед закриваючим тегом </body>." },
            ].map((cms, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "12px 18px", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                <span style={{ fontSize: 16 }}>{cms.icon}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{cms.cms}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{cms.steps}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Where on page */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border}`, fontSize: 14, fontWeight: 700 }}>Де на сторінці з'явиться кокон</div>
            <div style={{ padding: "14px 18px" }}>
              <div style={{ fontSize: 12, color: t.text, lineHeight: 1.7, marginBottom: 12 }}>
                Блок <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>&lt;div id="spalah-seo"&gt;&lt;/div&gt;</code> — це місце де з'явиться кокон. Розмістіть його там де хочете бачити блок посилань:
              </div>
              {[
                { place: "Перед футером", desc: "Рекомендовано. Після контенту, перед <footer>", rec: true },
                { place: "Після тексту сторінки", desc: "Після основного тексту, перед відгуками або схожими товарами" },
                { place: "В сайдбарі", desc: "Якщо є бокова панель — блок адаптується під ширину" },
              ].map((opt, i) => (
                <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", alignItems: "center" }}>
                  {opt.rec && <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(16,185,129,0.1)", color: "#10b981" }}>рек.</span>}
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{opt.place}</span>
                  <span style={{ fontSize: 11, color: t.textMuted }}>— {opt.desc}</span>
                </div>
              ))}
              <pre style={{ fontSize: 11, lineHeight: 1.5, padding: "12px 14px", borderRadius: 8, background: dark ? "#0a0a10" : "#f8f9fb", color: dark ? "#a0a0b8" : "#555", overflow: "auto", margin: "12px 0 0", whiteSpace: "pre-wrap" }}>{`<div class="page-content">
  <h1>...</h1>
  <div class="content">...</div>
</div>

<!-- ✅ SEO Кокон тут -->
<div id="spalah-seo"></div>

<footer>...</footer>

<!-- SDK скрипт перед </body> -->
${sdkLine}`}</pre>
            </div>
          </div>

          {/* Important */}
          <div style={{ ...card, padding: "14px 18px", background: dark ? "rgba(245,158,11,0.04)" : "rgba(245,158,11,0.02)", border: `1px solid ${dark ? "rgba(245,158,11,0.12)" : "rgba(245,158,11,0.06)"}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b", marginBottom: 6 }}>Важливо</div>
            <div style={{ fontSize: 12, color: t.text, lineHeight: 1.7 }}>
              • Мета-теги (Title, Description) замінюються через JavaScript — Google бачить їх при рендерингу<br />
              • Кокон працює тільки якщо є <code style={{ background: dark ? "#252535" : "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>&lt;div id="spalah-seo"&gt;</code> на сторінці<br />
              • Мета замінюються на ВСІХ сторінках де є відповідний URL в семантиці Spalah<br />
              • Якщо для сторінки немає даних в Spalah — нічого не змінюється, оригінальні мета залишаються<br />
              • Дані кешуються 24 години в localStorage і оновлюються у фоні
            </div>
          </div>
        </>
      )}
    </div>
  );
}
